import { AudioTranscriptExpandedView } from '@modules/feedback/components/AudioType/AudioTranscriptPopper/AudioTranscriptExpandedView';
import { AudioTranscriptSubtitleView } from '@modules/feedback/components/AudioType/AudioTranscriptPopper/AudioTranscriptSubtitleView';
import { trackAudioFeedbackEvents } from '@modules/feedback/components/AudioType/utils';
import { getAllHighlights } from '@modules/feedback/components/FeedbackDetails/utils';
import useFeedbackListStore from '@modules/feedback/store/useFeedbackListStore';
import { Highlight } from '@modules/feedback/types';
import { trackCopyFeedbackContentEvents } from '@modules/feedback/utils/trackCopyFeedbackContentEvents';
import { lighten, Paper, Popper, Slide } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
} from 'v2/core/entities/feedback';

interface AudioTranscriptPopperProps {
  selectedLabels: FeedbackLabel[] | undefined;
  feedback: Feedback;
  showOriginalLanguage: boolean;
}

export const AudioTranscriptPopper = ({
  selectedLabels,
  feedback,
  showOriginalLanguage,
}: AudioTranscriptPopperProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popperEl, setPopperEl] = useState<HTMLDivElement | null>(null);
  const [popperScrollbarWidth, setPopperScrollbarWidth] = useState(0);

  const shouldOpenPopover = useFeedbackListStore(
    (state) => state.feedbacks[feedback.id]?.isTranscriptPopoverOpened ?? true,
  );
  const shouldExpandPopover = useFeedbackListStore(
    (state) =>
      state.feedbacks[feedback.id]?.isTranscriptPopoverExpanded ?? false,
  );
  const setIsTranscriptPopoverExpanded = useFeedbackListStore(
    (state) => state.setIsTranscriptPopoverExpanded,
  );

  useEffect(() => {
    const feedbackAudioPlayerEl = document.getElementById(
      'js-feedback-details-audio-player',
    );
    setAnchorEl(feedbackAudioPlayerEl);
  }, []);

  useEffect(() => {
    if (popperEl) {
      const scrollbarWidth = popperEl.offsetWidth - popperEl.clientWidth;
      setPopperScrollbarWidth(scrollbarWidth);
    }
  }, [popperEl]);

  if (
    feedback.content.type !== FeedbackContentType.Audio ||
    anchorEl === null
  ) {
    return null;
  }

  const highlights: { [key: string]: Highlight[] } = selectedLabels?.length
    ? getAllHighlights(selectedLabels)
    : {};

  const togglePopper = (shouldExpand: boolean) => {
    setIsTranscriptPopoverExpanded(shouldExpand, feedback.id);
    trackAudioFeedbackEvents({
      eventName: shouldExpand
        ? 'Audio Feedback - Transcript: Expanded To Full View'
        : 'Audio Feedback - Transcript: Collapsed To Subtitle View',
      feedbackID: feedback.id,
    });
  };

  const popperWidth =
    (anchorEl?.offsetWidth ?? 300) +
    (shouldExpandPopover ? popperScrollbarWidth : 0);

  return (
    <Popper
      className="audio-transcript-popper-js"
      disablePortal
      transition
      open={shouldOpenPopover}
      anchorEl={anchorEl}
      placement="top-end"
      ref={(ref) => setPopperEl(ref)}
      sx={{
        width: `${popperWidth}px`,
        backgroundColor: 'background.default',
        zIndex: 1000,
        transition: 'max-height 0.2s ease-in-out',
        borderRadius: 2,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }}
    >
      {({ TransitionProps }) => (
        <Slide {...TransitionProps} direction="up">
          <Paper
            elevation={3}
            sx={{
              p: 1,
              borderRadius: 2,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderBottom: 'none',
              bgcolor: shouldExpandPopover
                ? (theme) => lighten(theme.palette.background.default, 0.1)
                : undefined,
              backgroundImage: shouldExpandPopover ? 'none' : undefined,
              boxShadow: shouldExpandPopover
                ? '0px -2px 6px -2px rgba(0,0,0,0.2)'
                : 'none',
            }}
            onCopy={() => {
              trackCopyFeedbackContentEvents({
                eventName: 'Copied Text From Feedback Content',
                feedbackID: feedback.id,
                feedbackType: feedback.content.type,
                contentType: 'raw',
              });
            }}
          >
            {shouldExpandPopover ? (
              <AudioTranscriptExpandedView
                feedback={feedback}
                highlights={highlights}
                showOriginalLanguage={showOriginalLanguage}
                onCollapse={() => togglePopper(false)}
                selectedLabels={selectedLabels}
              />
            ) : (
              <AudioTranscriptSubtitleView
                feedback={feedback}
                highlights={highlights}
                onExpand={() => togglePopper(true)}
              />
            )}
          </Paper>
        </Slide>
      )}
    </Popper>
  );
};
